import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Check, CheckCheck, Trash2, ExternalLink, Settings, X, Info, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { formatDistanceToNow } from 'date-fns';

const typeIcons = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
  system: Bell
};

const typeColors = {
  info: 'text-blue-400 bg-blue-500/10',
  success: 'text-green-400 bg-green-500/10',
  warning: 'text-yellow-400 bg-yellow-500/10',
  error: 'text-red-400 bg-red-500/10',
  system: 'text-primary bg-primary/10'
};

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const dropdownRef = useRef(null);
  
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    pushSupported,
    pushEnabled,
    subscribeToPush,
    unsubscribeFromPush
  } = useNotifications();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-96 max-h-[70vh] bg-card-bg border border-border rounded-xl shadow-2xl overflow-hidden z-[9999]"
            data-testid="notification-dropdown"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <h3 className="font-heading font-semibold text-white">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="p-1.5 text-white/40 hover:text-primary hover:bg-white/5 rounded transition-colors"
                    title="Mark all as read"
                  >
                    <CheckCheck size={16} />
                  </button>
                )}
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className={`p-1.5 rounded transition-colors ${showSettings ? 'text-primary bg-primary/10' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
                  title="Settings"
                >
                  <Settings size={16} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 text-white/40 hover:text-white hover:bg-white/5 rounded transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Settings Panel */}
            <AnimatePresence>
              {showSettings && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-b border-border overflow-hidden"
                >
                  <div className="p-4 bg-background/50">
                    <h4 className="text-sm font-medium text-white mb-3">Push Notifications</h4>
                    {pushSupported ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white/80 text-sm">Browser notifications</p>
                          <p className="text-white/40 text-xs">Receive alerts even when not using the app</p>
                        </div>
                        <button
                          onClick={pushEnabled ? unsubscribeFromPush : subscribeToPush}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            pushEnabled 
                              ? 'bg-white/5 text-white/60 hover:bg-white/10' 
                              : 'bg-primary text-white hover:bg-primary/90'
                          }`}
                        >
                          {pushEnabled ? 'Disable' : 'Enable'}
                        </button>
                      </div>
                    ) : (
                      <p className="text-white/40 text-sm">Push notifications are not supported in this browser</p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-white/40">
                  <Bell size={32} className="mb-3 opacity-50" />
                  <p className="text-sm">No notifications yet</p>
                </div>
              ) : (
                <div>
                  {notifications.map((notification) => {
                    const IconComponent = typeIcons[notification.type] || typeIcons.info;
                    const colorClass = typeColors[notification.type] || typeColors.info;
                    
                    return (
                      <motion.div
                        key={notification.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className={`group relative p-4 border-b border-border/50 hover:bg-white/5 transition-colors ${
                          !notification.is_read ? 'bg-primary/5' : ''
                        }`}
                        data-testid={`notification-${notification.id}`}
                      >
                        {/* Unread indicator */}
                        {!notification.is_read && (
                          <span className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-primary rounded-full" />
                        )}
                        
                        <div className="flex gap-3">
                          {/* Icon */}
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                            <IconComponent size={16} />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium leading-tight">
                              {notification.title}
                            </p>
                            <p className="text-white/60 text-xs mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                            <p className="text-white/40 text-xs mt-1">
                              {formatTime(notification.created_at)}
                            </p>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {notification.link && (
                              <a
                                href={notification.link}
                                className="p-1 text-white/40 hover:text-primary hover:bg-primary/10 rounded"
                                title="View"
                              >
                                <ExternalLink size={14} />
                              </a>
                            )}
                            {!notification.is_read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="p-1 text-white/40 hover:text-green-400 hover:bg-green-500/10 rounded"
                                title="Mark as read"
                              >
                                <Check size={14} />
                              </button>
                            )}
                            <button
                              onClick={() => archiveNotification(notification.id)}
                              className="p-1 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-border bg-background/50">
                <a
                  href="/dashboard/notifications"
                  className="block text-center text-primary text-sm hover:underline"
                >
                  View all notifications
                </a>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
